// AppStore.js
import _extend from 'lodash/extend'

// Get EventEmitter from polyfills (browser-compatible)
const getEventEmitter = () => {
  if (typeof window !== 'undefined' && window.EventEmitter) {
    return window.EventEmitter
  }
  // Fallback: create a simple EventEmitter if not available
  class SimpleEventEmitter {
    constructor () {
      this.events = {}
    }

    on (event, listener) {
      if (!this.events[event]) {
        this.events[event] = []
      }
      this.events[event].push(listener)
      return this
    }

    emit (event, ...args) {
      if (this.events[event]) {
        this.events[event].forEach(listener => listener(...args))
      }
      return this
    }

    removeListener (event, listenerToRemove) {
      if (this.events[event]) {
        this.events[event] = this.events[event].filter(listener => listener !== listenerToRemove)
      }
      return this
    }

    removeAllListeners (event) {
      if (event) {
        delete this.events[event]
      } else {
        this.events = {}
      }
      return this
    }
  }
  return SimpleEventEmitter
}

const EventEmitter = getEventEmitter()

const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone

export default _extend(new EventEmitter(), {
  // Initial data
  data: {
    brand: {},
    customer_id: false,
    customer: {
      data: {},
      ready: false,
      changing: false,
      changingName: 'Customer Change',
    },
    searchedCustomer: {
      data: {},
      ready: false,
    },
    customers: {
      data: {},
      ready: false,
    },
    customerApiKeys: {
      data: {},
      ready: false,
    },
    deviceSettings: {
      devices: [],
      newSettings: [],
      newDeviceInfo: {},
      saved: false,
      invalid: false,
    },
    deviceSettingHistory: {
      data: {},
      ready: false,
    },
    deviceStatus: {},
    doc: undefined,
    docs: {
      docs: [],
      docsReady: false,
    },
    errors: {
      connectionIssues: {
        count: 0,
        lastError: '',
      },
      // pathname of the page whose device the user is not allowed to see (group restriction)
      deviceAccessDenied: null,
    },
    globals: {},
    group: undefined,
    groups: {
      groups: [],
      groupsReady: false,
    },
    groupDevices: {},
    searchedGroups: {
      groups: [],
      groupsReady: false,
    },
    history: {
      past: [],
      list: '',
    },
    lookup: {},
    lookupReady: false,
    metric: [],
    metricReady: false,
    notifications: {
      popUp: {
        text: '',
        visible: false,
        logout: false,
      },
    },
    pinCode: {
      status: 'noPin',
      cancelled: false,
      code: '',
    },
    pollInterval: 5000,
    reading: {},
    readingReady: false,
    sort: {
      direction: '1',
      value1: 'physical',
      value2: 'deviceNumber',
    },
    stats: [],
    flowStats: {},
    flowReady: 'notLoaded',
    statsLookup: {},
    statsReady: 'notLoaded',
    rules: {
      alertsReady: false,
      rules: [],
      rule: {},
      ruleReady: false,
      newRule: {
        active: 'true',
        address: '',
        next_notification: '0',
        rule: {},
        transport: {},
        type: '',
        recurrence: {
          schedule: 'none',
          timezone,
        }
      },
      invalid: false,
      invalidContactInfo: true,
      saved: false,
    },
    user: {
      profile: {},
      data: {
        language: 'en',
        user_customers: [],
      },
      locale: {
        tempConv: 'f',
      },
      permissions: {
        level: 0,
      },
      ready: false,
      saved: false,
      onboarding: false,
    },
    searchedUser: [],
    userCustomers: [],
    userCustomersReady: false,
    userCustomer: {},
    customerUsers: [],
    deviceReading: [],
    deviceReadingReady: false,
  },

  // names for pages
  pages: {
    CustomerReportSettingsPage: 'Report Settings',
    CustomerSettingsPage: 'Settings',
    Alert: 'Device Alert',
    CustomerDeviceGroupsPage: 'Device Groups',
    CustomerDeviceGroupAddPage: 'Add Group',
    CustomerDeviceGroupEditPage: 'Update Group',
    CustomerUsersPage: 'Users',
    DeviceAdvancedSettings: 'Adv Settings',
    DeviceConfigure: 'Device Configure',
    DeviceList: 'Devices',
    DocPage: 'Online Manuals',
    ApiKeyPage: 'Altrac API Keys',
    DocViewPage: 'View Online Manual',
    Home: 'Altrac App',
    Login: 'Altrac Login',
    MoistureCombinedSettingsPage: 'Moisture Sensor Settings',
    PumpInterfaceSettingsPage: 'Pump Interface Settings',
    FlowInterfaceSettingsPage: 'Flow Interface Settings',
    PasswordNew: 'Create Password',
    SchedulePage: 'Schedule Device',
    SettingPage: 'Change Device Settings',
    Signup: 'Welcome to Altrac',
    Telemetry: 'Telemetry',
    UserPage: 'User Profile',
    UserGroupsPage: 'Add Group',
    UserEditPage: 'User Update',
    UserInvite: 'Invite User',
    UserPreferences: 'User Preferences',
    ViewMetric: 'View Metric',
  },

  auth: {
    loggedIn: null,
    idToken: '',
    idTokenExpiry: '',
    user: {},
  },

  // Add change listener
  addChangeListener (callback) {
    this.on('change', callback)
  },

  // Emit Change event
  emitChange () {
    this.emit('change')
  },

  getData () {
    return this.data
  },

  getBrand () {
    return this.data.brand
  },

  getCustomer () {
    return this.data.customer.data
  },

  getSearchedCustomer () {
    return this.data.searchedCustomer.data
  },

  getCustomers () {
    return this.data.customers.data.items
  },

  getCustomerBrand () {
    if (
      this.data.customer.data.brand !== null &&
      typeof this.data.customer.data.brand === 'object'
    ) {
      return this.data.customer.data.brand
    }
    return this.getBrand()
  },

  getGroup () {
    return this.data.group
  },

  getGroups () {
    return this.data.groups
  },

  resetGroups () {
    this.data.groups = []
    this.emitChange()
  },

  getSearchedGroups () {
    return this.data.searchedGroups
  },

  getRule () {
    return this.data.rules.rule
  },

  getRules () {
    return this.data.rules.rules
  },

  getNewRule () {
    return this.data.rules.newRule
  },

  getUser () {
    return this.data.user.data
  },

  getUserCustomer () {
    return this.data.userCustomer
  },

  /**
   * @returns logged in User's user_customers
   */
  getUserCustomers () {
    return this.data.user.data.customers
  },

  getUserGroups () {
    return this.data.userGroups
  },

  // Remove change listener
  removeChangeListener (callback) {
    this.removeListener('change', callback)
  },
})
